import cloneDeep from "lodash.clonedeep";
import { PromiseQueue, QueueablePromise } from "../../util/PromiseQueue";
import { Pith } from "pith";
import { generateCommands } from "../../generation";
import { DesignInputStep } from "../../generation/types";
import type { RenderableContext, RenderableContextService } from "@spiffcommerce/preview";
import { ILayout, LayoutRenderingPurpose, LayoutsState, StepType, Workflow } from "../../types";
import { createCanvas, getDomParser, loadImage } from "../../customCanvas";
import { getSvgElement, LayoutData } from "../../CommandContext";
import { renderPapyrusComponentAsString } from "../../util/crossplatform";
import { CreateLayoutCommand, GroupCommand } from "../../command";
import { WorkflowManager } from "../..";

export class LayoutPreviewService implements RenderableContextService {
    private readonly layouts: ReadonlyMap<string, LayoutPreviewBridge>;
    private workflowManager?: WorkflowManager;
    private handleCompleteRender: null | ((layouts: LayoutPreviewBridge[]) => void);

    constructor(layouts: ILayout[]) {
        this.handleCompleteRender = null;
        const map: Map<string, LayoutPreviewBridge> = new Map();
        layouts.forEach((layout) =>
            map.set(
                layout.id,
                new LayoutPreviewBridge(
                    layout.id,
                    layout.name,
                    this,
                    { width: layout.width, height: layout.height },
                    this.getWorkflowManager.bind(this),
                ),
            ),
        );
        this.layouts = map as ReadonlyMap<string, LayoutPreviewBridge>;
    }

    setCompleteRenderCallback(handleCompleteRender: (layouts: LayoutPreviewBridge[]) => void) {
        this.handleCompleteRender = handleCompleteRender;
        handleCompleteRender(Array.from(this.layouts.values()));
    }

    onCompleteRender() {
        this.handleCompleteRender && this.handleCompleteRender(Array.from(this.layouts.values()));
    }

    getAll(): ReadonlyMap<string, LayoutPreviewBridge> {
        return this.layouts;
    }

    setWorkflowManager(workflowManager: WorkflowManager) {
        this.workflowManager = workflowManager;
    }

    getWorkflowManager() {
        return this.workflowManager;
    }
}

/**
 * Determines wether power of two textures are supported based on
 * the availability of WebGL 2 on the target platform.
 *
 * Check WebGL2 support. Older Safari does not support WebGL2 and behaves badly when non POT textures are used.
 * If we fail to resolve a WebGL2 context we disable non power of two rendering.
 *
 * NOTE: DO NOT remove this without first testing behaviour against Safari on macOS Big Sur & mobile!!!
 */
const calculateNonPOTSupport = (): Promise<boolean> => {
    return new Promise((resolve, _reject) => {
        try {
            const glEl = createCanvas();
            const gl = glEl.getContext("webgl2");
            resolve(!!gl);
        } catch (e) {
            resolve(false);
        }
    });
};
export const nonPotTexSupport: Promise<boolean> = calculateNonPOTSupport();

/**
 * The panel canvas class that stores both the main rendering canvas as well as rendering context
 * for a rendering context
 */
export class LayoutPreviewBridge implements RenderableContext {
    hasSetStaticContext: boolean = false;

    private readonly id: string;
    private readonly name: string;
    private readonly panelSize: { width: number; height: number };
    private readonly getWorkflowManager?: () => WorkflowManager | undefined;

    private service: LayoutPreviewService;

    private interactiveDirty: boolean = false;

    // Texture context & associated dirty flag.
    private textureCtx?: CanvasRenderingContext2D;
    private staticCtxDirty: boolean = false;
    private lastRequestedRenderArguments: LayoutData[] | undefined = undefined;

    // Stores a timestamp for the last static render.
    private lastCompletedStaticRender: number | undefined = undefined;

    // A render queue for this layout.
    // No more than 2 renders can be queued at any time.
    // Beyond this, renders will be discarded before they can execute
    private renderQueue: PromiseQueue<RenderQueuePromise> = new PromiseQueue(2);

    constructor(
        id: string,
        name: string,
        service: LayoutPreviewService,
        panelSize: { width: number; height: number },
        getWorkflowManager?: () => WorkflowManager | undefined,
    ) {
        this.id = id;
        this.name = name;
        this.service = service;
        this.panelSize = panelSize;
        this.getWorkflowManager = getWorkflowManager;
    }

    getID(): string {
        return this.id;
    }

    getName(): string {
        return this.name;
    }

    getPanelSize(): { width: number; height: number } {
        return this.panelSize;
    }

    getStaticContext(): CanvasRenderingContext2D | undefined {
        return this.textureCtx;
    }

    /**
     * Register canvas to be rendered to.
     */
    setStaticContext(ctx: CanvasRenderingContext2D) {
        this.textureCtx = ctx;
        if (this.lastRequestedRenderArguments) {
            this.render(this.lastRequestedRenderArguments);
        }
    }

    getStaticContextDirty(): boolean {
        return this.staticCtxDirty;
    }

    setStaticContextDirty(dirty: boolean): void {
        this.staticCtxDirty = dirty;
    }

    getInteractiveCanvasDirty() {
        return this.interactiveDirty;
    }

    setInteractiveCanvasDirty(dirty: boolean) {
        this.interactiveDirty = dirty;
    }

    markLastCompletedStaticRender() {
        this.lastCompletedStaticRender = Date.now();
        this.service.onCompleteRender();
    }

    /**
     * Returns a timestamp for the last time that this canvas rendered to its dynamic texture in the
     * form of Date.now(). If this panel has never rendered undefined will be returned.
     */
    getLastCompletedStaticRender() {
        return this.lastCompletedStaticRender;
    }

    /**
     * Actions to perform when a static render event is fired for this canvas.
     */
    async render(layouts: LayoutData[]) {
        const clonedLayouts = cloneDeep(layouts);

        // We store the parameters for this render so that we can re-render
        // if the static context is set later.
        this.lastRequestedRenderArguments = clonedLayouts;

        // Pull the static context and handle the case where it isn't available yet.
        const staticContext = this.getStaticContext();
        if (!staticContext) {
            this.markLastCompletedStaticRender();
            this.setStaticContextDirty(false);
            return;
        }

        // Queue the render.
        this.renderQueue.enqueue(
            new RenderQueuePromise(
                this.getID(),
                staticContext,
                await nonPotTexSupport,
                () => {
                    this.markLastCompletedStaticRender();
                    this.setStaticContextDirty(true);
                },
                clonedLayouts,
                this.getWorkflowManager,
            ),
        );
    }
}

/**
 * Encapsulates the behavior required to render a layout to a canvas. The promise can
 * be generated at any point in time allowing for queue systems to process the render
 * once at a time and enforce ordering rules.
 */
class RenderQueuePromise extends QueueablePromise {
    private readonly layoutId: string;
    private readonly ctx: CanvasRenderingContext2D;
    private readonly nonPOTSupport: boolean;
    private readonly onRender: () => void;
    private readonly layouts: LayoutData[]; // TODO: Do we need to pass this whole array in?
    private readonly getWorkflowManager?: () => WorkflowManager | undefined;

    constructor(
        layoutId: string,
        ctx: CanvasRenderingContext2D,
        nonPOTSupport: boolean,
        onRender: () => void,
        layouts: LayoutData[],
        getWorkflowManager?: () => WorkflowManager | undefined,
    ) {
        super();
        this.layoutId = layoutId;
        this.ctx = ctx;
        this.nonPOTSupport = nonPOTSupport;
        this.onRender = onRender;
        this.layouts = layouts;
        this.getWorkflowManager = getWorkflowManager;
    }

    /**
     * Returns the resolution expected for generated textures.
     * FIXME: This is reimplemented from the preview package. Can we find a way to remove this duplication.
     */
    private getDynamicTextureResolution() {
        return this.getIsMobile() || !this.offscreenRenderingSupported()
            ? {
                  width: 1024,
                  height: 1024,
              }
            : {
                  width: 2048,
                  height: 2048,
              };
    }

    private getIsMobile() {
        return window.innerWidth <= 480;
    }

    /**
     * Returns true when multithreaded rendering is supported.
     */
    private offscreenRenderingSupported(): boolean {
        // Samsung browser has broken web worker custom fonts from v14. To be
        // safe, if we detect this browser we immediately disable offscreen canvas. There
        // may be a more specific feature we can detect to be more clean about this moving forwards.
        if (navigator.userAgent.includes("SamsungBrowser")) {
            return false;
        }
        // Otherwise we check for relevant required functionality
        return !!window.Worker && !!(window as any).OffscreenCanvas;
    }

    private resizeFit(
        originalSize: { width: number; height: number },
        maxSize = 4096,
    ): { width: number; height: number } {
        const widthRatio = maxSize / originalSize.width;
        const heightRatio = maxSize / originalSize.height;
        const minAspectRatio = Math.min(widthRatio, heightRatio);
        if (minAspectRatio > 1) {
            return originalSize;
        }

        return { width: originalSize.width * minAspectRatio, height: originalSize.height * minAspectRatio };
    }

    /**
     * Render the internally stored layout to the canvas.
     * NOTE: This function is performance critical code. Please throughly test any changes to
     * ensure performance isn't lost and that rendering behavior is preserved when modifying this logic.
     * The aim is to have this code rendering in less than 16.66ms (60fps) on modern hardware. This is
     * a loose target but should be kept in mind when making changes.
     */
    public async execute() {
        // Ensure layout is available. Cancel out of render otherwise.
        const layout = this.layouts.find((layout) => layout.layoutState.layout.id === this.layoutId);
        if (!layout) {
            return;
        }
        const elements = layout.layoutState.elements || [];

        const layoutWidth = layout.layoutState.layout.width;
        const layoutHeight = layout.layoutState.layout.height;

        const renderDims =
            layoutWidth === 1 || layoutHeight === 1 ? { width: 1, height: 1 } : this.getDynamicTextureResolution();

        // We need to ensure that we compute the correct width and height for the final texture
        // based on whether or not we know the platform we're running on support non power of two
        const targetWidth = renderDims.width;
        const targetHeight = renderDims.height;
        let width: number;
        let height: number;

        if (this.nonPOTSupport) {
            const targetRatio = targetWidth / targetHeight;
            const ratio = layoutWidth / layoutHeight;
            if (ratio < targetRatio) {
                width = targetWidth;
                height = layoutHeight * (targetWidth / layoutWidth);
            } else {
                width = layoutWidth * (targetHeight / layoutHeight);
                height = targetHeight;
            }

            const boundedSize = this.resizeFit({ width, height });
            width = boundedSize.width;
            height = boundedSize.height;
        } else {
            width = renderDims.width;
            height = renderDims.height;
        }

        // Do the render with the SVG we have now generated
        const templatingContext = await this.getWorkflowManager?.()?.getTemplatingContext();
        const svgElement = getSvgElement(layout.layoutState.layout, elements, {
            renderingConfiguration: {
                purpose: LayoutRenderingPurpose.ThreeD,
                templatingContext,
            },
        });
        const svgStr = renderPapyrusComponentAsString(svgElement);
        const cvg = await Pith.from(this.ctx, svgStr, {
            anonymousCrossOrigin: true,
            ignoreDimensions: !this.nonPOTSupport,
            ignoreAnimation: true,
            ignoreClear: true,
            ignoreMouse: true,
            enableRedraw: false,
            createCanvas: createCanvas as any,
            createImage: loadImage as any,
            DOMParser: getDomParser(),
            fetch: fetch,
        });
        cvg.resize(width, height);
        await cvg.render();
        // Callback to notify the render has completed.
        this.onRender();
    }
}

interface StepAspectValue {
    stepName: string;
    stepAspectType: string;
    value?: string;
}
export const stepAspectValuesToDesignInputSteps = (
    stepAspectValues: StepAspectValue[],
    workflow: Workflow,
): DesignInputStep[] => {
    const designInputSteps: DesignInputStep[] = [];
    stepAspectValues.forEach((stepAspectValue) => {
        const step = workflow.steps.find((step) => step.stepName === stepAspectValue.stepName);
        if (step?.type === StepType.Text && stepAspectValue.stepAspectType === "Text") {
            designInputSteps.push({
                name: step.stepName,
                data: {
                    text: stepAspectValue.value,
                },
            });
        }
    });
    return designInputSteps;
};

export const generateStateFromDesignInputSteps = async (
    designInputSteps: DesignInputStep[],
    workflow: Workflow,
    layouts: ILayout[],
    productOverlayImageUrl?: string,
): Promise<LayoutsState> => {
    let state: LayoutsState = {
        serializableWorkflow: { steps: [] },
        layouts: {},
    };
    // Build base layouts.
    const layoutGroup = new GroupCommand(layouts.map((layout) => new CreateLayoutCommand(layout)));
    state = layoutGroup.apply(state);
    // Build base commands
    const commands = await generateCommands(designInputSteps, workflow, layouts, productOverlayImageUrl);
    const commandGroup = new GroupCommand(commands);
    return commandGroup.apply(state);
};
